// ---------------------------------------------------------------------------
// Prezio Onboard Widget — API Types
// ---------------------------------------------------------------------------
// All types matching the Prezio Onboard REST API contract (Section 6).
// ---------------------------------------------------------------------------

/** All possible step identifiers in the onboarding flow. */
export type StepId =
  | 'country'
  | 'local_consumer_type'
  | 'address'
  | 'address_validation'
  | 'dso_org'
  | 'dso_main_tariff'
  | 'dso_tariff'
  | 'dso_properties'
  | 'retailer_org'
  | 'retailer_main_tariff'
  | 'retailer_signup_date'
  | 'retailer_tariff'
  | 'retailer_properties'
  | 'retailer_custom_type'
  | 'retailer_custom_properties'
  | 'retailer_custom_prices'
  | 'authorities_conditions'
  | 'authorities'
  | 'summary';

/** Possible session lifecycle states. */
export type SessionStatus =
  | 'created'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'expired'
  | 'failed';

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

/** Progress information returned with every session response. */
export interface SessionProgress {
  /** Steps the user has already completed. */
  completed_steps: StepId[];
  /** Steps still remaining in the flow. */
  remaining_steps: StepId[];
  /** Overall completion percentage (0–100). */
  percentage: number;
}

/** Pointer to the next step the widget should render. */
export interface NextStep {
  /** The step to render next. */
  step_id: StepId;
  /** Whether the step is required or can be skipped by the user. */
  required: boolean;
  /** Absolute URL to fetch options for this step — use directly, don't construct. */
  options_url: string;
}

/** Result payload returned when a session completes successfully. */
export interface SessionResult {
  /** UUID of the created location. */
  location_id: string;
  /** Prefixed tariff IDs assigned to this location (e.g. "tar_101"). */
  tariff_ids: string[];
}

/** Full session object returned by the API. */
export interface OnboardSession {
  /** Session UUID. */
  id: string;
  /** Current lifecycle status. */
  status: SessionStatus;
  /** The step the user is currently on, or `null` if completed / terminal. */
  current_step: StepId | null;
  /** All data collected so far, keyed by step. */
  collected_data: Record<string, unknown>;
  /** Pre-existing location to update (if any). */
  target_location_id: string | null;
  /** Location ID created on completion. */
  result_location_id: string | null;
  /** Backend error message (empty string when no error). */
  error_message: string;
  /** Step progress information. */
  progress: SessionProgress;
  /** Next step to render, or `null` when session is in a terminal state. */
  next_step: NextStep | null;
  /** Result payload, available after successful completion. */
  result: SessionResult | null;
  /** Whether the session token has expired. */
  is_expired: boolean;
  /** ISO 8601 timestamp when the session expires. */
  expires_at: string;
  /** ISO 8601 timestamp when the session was created. */
  created_at: string;
  /** ISO 8601 timestamp of the last update. */
  updated_at: string;
  /** ISO 8601 timestamp of completion, or `null`. */
  completed_at: string | null;
  /**
   * Steps whose values were pre-set during session creation (e.g. country,
   * local_consumer_type). These steps are invisible to the user: they are
   * excluded from navigation, the progress bar, and the back button logic.
   * Defaults to `[]` when the backend omits the field.
   */
  predefined_steps: StepId[];
  /**
   * Human-readable display names for values stored in collected_data.
   * Keyed by a step-level identifier (e.g. `"country"`, `"dso_org"`).
   * Populated by the backend so the widget can show friendly names without
   * re-fetching step options on session reopen.
   *
   * Example keys:
   *  - `"country"` → `"Sweden"`
   *  - `"local_consumer_type"` → `"Household"`
   *  - `"dso_org"` → `"Vattenfall Eldistribution AB"`
   *  - `"dso_tariff"` → `"Standard Grid Tariff"`
   *  - `"retailer_org"` → `"Fortum Sverige AB"`
   *  - `"retailer_tariff"` → `"Green Energy Standard"`
   *  - `"authority.energy_tax"` → `"Energy Tax"`
   */
  display_values: Record<string, string>;
  /**
   * Step IDs in the current flow that the user cannot navigate back to
   * (predefined or auto-skipped because only one option was available).
   * Consumers should hide any "edit" affordance for these steps.
   * Defaults to `[]` when the backend omits the field.
   */
  non_editable_steps?: StepId[];
  /**
   * Machine-readable codes signalling sandbox/dev data gaps. The widget
   * renders a localized notice for each code; always empty in production.
   *
   * Supported codes:
   *  - `"dso_tariffs_unavailable"` — the consumer type expects a grid (DSO)
   *    tariff but none is available in the curated dataset. Either no
   *    sandbox-visible DSO covers the address (the whole DSO branch is
   *    dropped, so `collected_data.dso` is empty) or the resolved DSO has no
   *    applicable published tariffs. Never emitted when the consumer type
   *    has no DSO step at all.
   */
  sandbox_notices?: string[];
  /**
   * Languages the widget may offer for the session's country, besides
   * English (backend codes, e.g. `["fr", "nl"]` for Belgium). Empty until a
   * country is set. Defaults to `[]` when the backend omits the field.
   */
  country_languages?: string[];
  /**
   * The session language (backend code, e.g. `"da"`, `"no"`), or `null`.
   * Set by the integrator or by the widget's language menu; the widget opens in it.
   */
  locale?: string | null;
}

// ---------------------------------------------------------------------------
// Step Options
// ---------------------------------------------------------------------------

/** Assignment type for authority tariff mappings. */
export type TariffAssignment = 'mandatory' | 'default' | 'conditional' | 'optional';

/** Direction specifier for authority tariff mappings. */
export type TariffDirection = 'BOT' | 'IMP' | 'EXP';

/** Condition metadata on a CONDITIONAL authority tariff. */
export interface TariffCondition {
  /** The property key whose answer gates this tariff. */
  property_key: string;
  /** The LocalTariffProperty id that must have been selected. */
  ltp_id: number;
}

/** A single selectable option within a step. */
export interface StepOption {
  /** Prefixed identifier (e.g. "org_123", "tar_456", "lct_789"). */
  id: string;
  /** Display name. */
  name: string;
  /** Optional description text. */
  description: string;
  /** Type-specific extra data (logos, flags, tariff types, etc.). */
  metadata: Record<string, unknown>;

  // -- Authority-specific fields (only present on `authorities` step options) --

  /** Assignment type — only on authorities options. */
  assignment?: TariffAssignment;
  /** Whether the user can uncheck this tariff — only on authorities options. */
  removable?: boolean;
  /** Import/export direction — only on authorities options. */
  direction?: TariffDirection;
  /** Condition that gates this tariff — only on CONDITIONAL authorities options. */
  condition?: TariffCondition | null;
}

/** Coverage information. Present only when the user sees fewer options than
 *  exist — either because of sandbox curation or their plan. The widget never
 *  checks environment itself. */
export interface CoverageInfo {
  /** Number of options shown to the user. */
  shown: number;
  /** Total options that exist. */
  total: number;
  /** Why options are hidden. Absent on older backends — treat as `sandbox`. */
  reason?: 'sandbox' | 'plan';
}

/** Response from `GET /options/?step={step}`. */
export interface StepOptionsResponse {
  /** Which step these options belong to. */
  step: StepId;
  /** Available options the user can choose from. */
  options: StepOption[];
  /** Non-null when the backend auto-selected an option. */
  auto_select: StepOption | null;
  /** Whether the user must pick an option before proceeding. */
  selection_required: boolean;
  /** `true` when the backend already handled this step automatically. */
  skip_step: boolean;
  /** Extra context from backend rules (informational). */
  rule_context: Record<string, unknown>;
  /** Sandbox coverage data — absent in production or when counts match. */
  _coverage?: CoverageInfo | null;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** Response from `POST /validate/`. */
export interface ValidationResult {
  /** Whether the submitted data is valid. */
  valid: boolean;
  /** Per-field error messages. */
  errors: Record<string, string[]>;
  /** Non-blocking warnings. */
  warnings: string[];
}

// ---------------------------------------------------------------------------
// Submit Payloads — discriminated union
// ---------------------------------------------------------------------------

export interface CountrySubmitPayload {
  step: 'country';
  data: { country_code: string };
}

export interface ConsumerTypeSubmitPayload {
  step: 'local_consumer_type';
  data: { local_consumer_type_id: string };
}

export interface AddressSubmitPayload {
  step: 'address';
  data: {
    display: string;
    address1?: string;
    city?: string;
    postal_code?: string;
  };
}

export interface AddressValidationSubmitPayload {
  step: 'address_validation';
  data: {
    coordinates: { latitude: number; longitude: number };
  };
}

export interface OrganizationSubmitPayload {
  step: 'dso_org' | 'retailer_org';
  data: { organization_id: string; requested_org_name?: string };
}

export interface MainTariffSubmitPayload {
  step: 'dso_main_tariff' | 'retailer_main_tariff';
  data: { main_tariff_id: string };
}

export interface TariffEntry {
  tariff_id: string;
  /** Direction code. Defaults to "BOT" when omitted. */
  direction?: 'BOT' | 'IMP' | 'EXP';
}

export interface AuthorityEntry {
  authority_type: string;
  tariff_id: string;
  direction?: 'BOT' | 'IMP' | 'EXP';
}

export interface TariffSubmitPayload {
  step: 'dso_tariff' | 'retailer_tariff';
  /**
   * Single-pick payload retained for the current widget. The backend
   * also accepts the multi-pick form `{ tariffs: TariffEntry[] }`.
   */
  data: { tariff_id: string } | { tariffs: TariffEntry[] };
}

export interface PropertiesSubmitPayload {
  step: 'dso_properties' | 'retailer_properties';
  /** Dynamic keys: property_key → condition_value (strings). */
  data: Record<string, string>;
}

export interface AuthoritiesConditionsSubmitPayload {
  step: 'authorities_conditions';
  /** Dynamic keys: property prezio codes mapped to selected LTP ids. */
  data: Record<string, number>;
}

export interface AuthoritiesSubmitPayload {
  step: 'authorities';
  /** List of selected authority tariff entries. */
  data: { authorities: AuthorityEntry[] };
}

export interface CustomTypeSubmitPayload {
  step: 'retailer_custom_type';
  data: { custom_type: string; candidate_template_ids: string[] };
}

export interface CustomPropertiesSubmitPayload {
  step: 'retailer_custom_properties';
  data: { template_id: string } | { properties: Record<string, number> };
}

export interface CustomPricesSubmitPayload {
  step: 'retailer_custom_prices';
  data:
    | { price_value: number; vat_included: boolean }
    | { prices: Array<{ id: string; price_value: number }>; vat_included: boolean }
    | {
        components: Array<{ id: string; prices: Array<{ id: string; price_value: number }> }>;
        vat_included: boolean;
      };
}

export interface SummarySubmitPayload {
  step: 'summary';
  data: { confirmed: true };
}

export interface SignupDateSubmitPayload {
  step: 'retailer_signup_date';
  data: { signup_date: string };
}

/** Union of every valid step-submit payload shape. */
export type SubmitStepPayload =
  | CountrySubmitPayload
  | ConsumerTypeSubmitPayload
  | AddressSubmitPayload
  | AddressValidationSubmitPayload
  | OrganizationSubmitPayload
  | MainTariffSubmitPayload
  | TariffSubmitPayload
  | PropertiesSubmitPayload
  | AuthoritiesConditionsSubmitPayload
  | CustomTypeSubmitPayload
  | CustomPropertiesSubmitPayload
  | CustomPricesSubmitPayload
  | SignupDateSubmitPayload
  | AuthoritiesSubmitPayload
  | SummarySubmitPayload;

// ---------------------------------------------------------------------------
// Property Step Metadata
// ---------------------------------------------------------------------------

/** Metadata on a properties step option (dso_properties, retailer_properties, authorities_conditions). */
export interface PropertyOptionMetadata {
  property_key: string;
  property_name: string;
  question: string;
  condition_value: string;
  is_default: boolean;
}

// ---------------------------------------------------------------------------
// API Error
// ---------------------------------------------------------------------------

/** Structured error returned by the API client on non-2xx responses. */
export class ApiError extends Error {
  /** HTTP status code. */
  readonly status: number;
  /** Machine-readable error code (e.g. "validation_error", "authentication_failed"). */
  readonly code: string;
  /** Per-field validation errors (empty object when not applicable). */
  readonly fieldErrors: Record<string, string[]>;

  constructor(status: number, body: unknown) {
    const parsed = parseErrorBody(body);
    super(parsed.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = parsed.code;
    this.fieldErrors = parsed.fieldErrors;
  }
}

// -- helpers ----------------------------------------------------------------

interface ParsedError {
  message: string;
  code: string;
  fieldErrors: Record<string, string[]>;
}

function parseErrorBody(body: unknown): ParsedError {
  if (typeof body !== 'object' || body === null) {
    return { message: 'An unknown error occurred', code: 'unknown', fieldErrors: {} };
  }

  // Prezio API wraps errors in {"error": {...}} — unwrap if present
  const raw = body as Record<string, unknown>;
  const obj =
    typeof raw['error'] === 'object' && raw['error'] !== null
      ? (raw['error'] as Record<string, unknown>)
      : raw;

  const code = typeof obj['code'] === 'string' ? obj['code'] : 'unknown';
  const message =
    typeof obj['detail'] === 'string'
      ? obj['detail']
      : typeof obj['message'] === 'string'
        ? obj['message']
        : `Request failed with status`;

  let fieldErrors: Record<string, string[]> = {};
  if (typeof obj['errors'] === 'object' && obj['errors'] !== null) {
    fieldErrors = obj['errors'] as Record<string, string[]>;
  }

  return { message, code, fieldErrors };
}
